import { useQuery } from '@apollo/client'
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Grid2 as Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'
import {
  CATALOG_PAGE_SIZE,
  GET_INITIAL_CATALOG_VALUES,
  screenPath,
  setSessionData,
  cartLineItemsOf,
  toCartUnitCount,
  toCatalogCardModel,
  toCatalogItemInputs,
  toPriceText,
  toQuantitiesBySku,
  toSuggestedOrderModel,
  type IInitialCatalog,
  type IInitialCatalogVariables,
} from '@commerce/core'
import { YALO_RADIUS, YALO_SEMANTIC, YALO_SHADOW } from '@commerce/tokens'
import { BannerRail } from '../components/BannerRail'
import { BottomNav } from '../components/BottomNav'
import { CartGlyph, FilterGlyph } from '../components/icons'
import { CatalogCard } from '../components/CatalogCard'
import { CatalogSearch } from '../components/CatalogSearch'
import { CategoryChips } from '../components/CategoryChips'
import { FilterSheet } from '../components/FilterSheet'
import { StoryReel } from '../components/StoryReel'
import { StoryViewer } from '../components/StoryViewer'
import { RepeatLastOrder } from '../components/RepeatLastOrder'
import { SuggestedOrder } from '../components/SuggestedOrder'
import { useStorefrontConfig } from '../config/StorefrontConfigContext'
import { articlesText } from '../text'
import { useDemo, type Story } from '../demo'
import { Screen, Section } from '../design'
import { homeBanners, type BannerDestination } from '../home/banners'
import { pendingUnitsDelta, useDebouncedCartQuantity } from '../hooks/useDebouncedCartQuantity'
import { useScrolled } from '../hooks/useScrolled'
import { usePromotions } from '../hooks/usePromotions'
import { useAppDispatch } from '../hooks/useAppDispatch'
import { useAppSelector } from '../hooks/useAppSelector'
import { usePresentationContext } from '../hooks/usePresentationContext'
import { useBrandOptions } from '../hooks/useBrandOptions'
import { useCategoryOptions } from '../hooks/useCategoryOptions'
import { ALL, PROMOTIONS, useCatalogBrowse } from '../hooks/useCatalogBrowse'
import { useInfiniteScroll } from '../hooks/useInfiniteScroll'
import { usePrefillCart } from '../hooks/usePrefillCart'
import { useRepeatLastOrder } from '../hooks/useRepeatLastOrder'
import { useSuggestedBasket, type BasketSource } from '../hooks/useSuggestedBasket'

/**
 * What each recommendation field means, said in the storefront's voice. The
 * section used to claim "Según tu pedido del 6 ago" over a basket built from
 * the first three products of the catalog; now the sentence and the basket come
 * from the same place.
 */
const BASKET_SUBTITLE: Record<BasketSource, string> = {
  prefillCart: 'Tu pedido sugerido',
  buyAgain: 'Lo que sueles volver a pedir',
  suggestedItems: 'Sugerido para tu tienda',
}

/**
 * The home screen: header, reels, suggested order, category filter, grid, nav.
 *
 * It boots on `GET_INITIAL_CATALOG_VALUES` rather than the plan's `GET_CATALOG`
 * because the grid needs three things the storefront has to have before it can
 * paint honestly: the products, the cart (a card shows what is already in it),
 * and the session (whose customer decides whether prices are hidden at all).
 * That document exists precisely so those are one round trip instead of three.
 *
 * Two sources meet on this screen and the seam is deliberate: the grid, the
 * cart and every price come from commerce-scs through `@commerce/core`, while
 * the distributor and the stories come from `demo/` because no endpoint serves
 * them (`docs/NOT_PORTED.md`). Nothing local is ever added to a real total.
 *
 * Narrowing the grid — a category, a brand, typed text, the promo chip — is the
 * server's job now (`useCatalogBrowse`), and so is the second page. The page
 * itself only ever renders the blocks that hook hands it; what it used to do
 * locally could not see past the products the boot query happened to return.
 */
export const CatalogPage = () => {
  const { slug = '', sessionId = '' } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const presentation = usePresentationContext()
  const { pendingBySku, changeQuantity, error: updateError } = useDebouncedCartQuantity()
  const { addBasket, error: basketError, loading: addingBasket } = usePrefillCart()
  const promotionConfig = useAppSelector((state) => state.config.config)
  const { storeName, logoUrl } = useStorefrontConfig()
  const { distributor } = useDemo()

  const [storyIndex, setStoryIndex] = useState<number | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)

  const scrolled = useScrolled()

  // The reels: real storefront-wide promotions. No promotions means no rail
  // and no way into it — the "Promos" button in the nav goes with them.
  const { stories } = usePromotions()
  const hasStories = stories.length > 0

  const { data, loading, error } = useQuery<IInitialCatalog, IInitialCatalogVariables>(
    GET_INITIAL_CATALOG_VALUES,
    {
      variables: {
        storefrontName: slug,
        sessionUid: sessionId,
        catalogSessionUid: sessionId,
        // Page one, explicitly. Without a pagination argument the server
        // decides how much to send, and `useCatalogBrowse` would then ask for
        // "page two" of a page size it does not know — overlapping products at
        // best, skipped ones at worst.
        pagination: { pageNumber: 1, pageSize: CATALOG_PAGE_SIZE },
      },
    },
  )

  // Everything that narrows or extends the grid. The boot page above is its
  // first page, so choosing nothing costs no extra request.
  const browse = useCatalogBrowse({ bootBlocks: data?.catalog })
  const { categories } = useCategoryOptions()
  const { brands: brandOptions, loading: brandsLoading } = useBrandOptions(filtersOpen)

  // The session is what makes the per-customer hide-prices rule resolvable.
  // Until it lands, `hidePricesActive` is undefined and only the
  // storefront-wide switch applies.
  const session = data?.session
  useEffect(() => {
    if (session) dispatch(setSessionData(session))
  }, [dispatch, session])

  // The cart's lines, flat or grouped — a grouped storefront (`prueba`) would
  // otherwise leave every stepper at zero while the cart was really full.
  const cartLines = useMemo(() => cartLineItemsOf(data?.cart), [data])

  // What the cart has confirmed, then what the shopper has tapped on top of
  // it. The overlay is what makes a burst of taps visible click by click while
  // the debounced mutation waits for the burst to end.
  const committedBySku = useMemo(() => toQuantitiesBySku(cartLines), [cartLines])
  const quantitiesBySku = useMemo(
    () => ({ ...committedBySku, ...pendingBySku }),
    [committedBySku, pendingBySku],
  )

  // Every page of the current view, deduplicated by sku inside the mapper — a
  // product that belongs to two categories arrives twice per page.
  const items = useMemo(
    () =>
      toCatalogItemInputs({
        categories: browse.blocks,
        quantitiesBySku,
        config: promotionConfig,
        activePromotions: data?.cart?.activePromotions,
      }),
    [browse.blocks, quantitiesBySku, promotionConfig, data],
  )

  const visible = useMemo(
    () => items.map((item) => toCatalogCardModel(item, presentation)),
    [items, presentation],
  )

  const chips = useMemo(() => [ALL, PROMOTIONS, ...categories], [categories])

  /**
   * The storefront's banners, already on the wire: `sessionFieldsQuery` selects
   * them, so the boot query above has been fetching them all along. A separate
   * `banners(storefrontName, sessionUid)` query exists and would be a second
   * round trip for data this screen already holds.
   *
   * The clock is read here rather than on a timer: `now` only moves when the
   * session data or the categories do, so a banner that expires while the
   * shopper is looking at the page survives until the next fetch. A rail that
   * re-rendered every second to retire one banner would cost more than it
   * bought.
   */
  const banners = useMemo(
    () => homeBanners(session?.banners, { now: new Date(), categories }),
    [session, categories],
  )
  const hasBanners = banners.length > 0

  // The basket the storefront proposes, and the quantities it proposed them in.
  // Nothing here is derived from the grid any more: the products are the ones
  // the recommendation field returned, which is why the section disappears when
  // a storefront has no recommendations rather than inventing one.
  const basket = useSuggestedBasket()

  // "Repetir mi último pedido". Null order means this customer has never
  // bought anything, and the card — date included — is not rendered at all.
  const repeatLastOrder = useRepeatLastOrder()

  const suggested = useMemo(() => {
    const basketItems = toCatalogItemInputs({
      categories: [{ category: 'Sugerido', productsPrices: basket.products }],
      quantitiesBySku,
      config: promotionConfig,
      activePromotions: data?.cart?.activePromotions,
    })
    return toSuggestedOrderModel(basketItems, basket.quantities, presentation)
  }, [basket.products, basket.quantities, quantitiesBySku, promotionConfig, data, presentation])

  // The sentinel asks for the next page while it is on screen. It is disabled
  // during a load, so one scroll to the bottom is one request.
  const sentinel = useInfiniteScroll(browse.hasMore && !browse.loadingMore, browse.loadMore)

  const gridEyebrow = browse.searching
    ? 'Resultados'
    : browse.category === ALL
      ? 'Productos'
      : browse.category

  /**
   * "20 productos" is a lie while there is a page 2 — it is what has been
   * loaded, not what matches. With more to come the count says so instead of
   * quietly under-reporting the catalog.
   */
  const gridCount = (() => {
    if (browse.loading) return ''
    const noun = visible.length === 1 ? '1 producto' : `${visible.length} productos`
    return browse.hasMore ? `${noun}+` : noun
  })()

  /** The badge is decorative, so the count has to be in the name — and agree. */
  const filtersLabel = (() => {
    if (browse.brands.length === 0) return 'Filtros'
    return browse.brands.length === 1 ? 'Filtros, 1 marca' : `Filtros, ${browse.brands.length} marcas`
  })()

  const emptyMessage = (() => {
    if (browse.searchTerm.trim() !== '') {
      return `No encontramos productos para “${browse.searchTerm.trim()}”.`
    }
    if (browse.brands.length > 0) return 'Ninguna de las marcas elegidas tiene productos aquí.'
    if (browse.category === PROMOTIONS) return 'No hay productos en promoción ahora mismo.'
    return 'No hay productos en esta categoría.'
  })()

  // Units, not lines: two lines of the same product are still two of it. The
  // pending delta keeps the badge moving with the taps, not with the network.
  const unitsInCart = useMemo(
    () => toCartUnitCount(cartLines) + pendingUnitsDelta(pendingBySku, committedBySku),
    [cartLines, pendingBySku, committedBySku],
  )

  /**
   * The whole basket in one mutation, and the cart only if it worked.
   *
   * This used to be a loop of `cartAddProduct` followed by an unconditional
   * navigation: a failed line was invisible, and the shopper landed on a cart
   * missing part of what they had just accepted.
   */
  const addSuggested = async () => {
    const added = await addBasket(suggested.lines)
    if (added) navigate(screenPath('cart', { slug, sessionId }))
  }

  /**
   * The card stepper. The card reports only the sku; the current quantity —
   * committed plus whatever this burst of taps already accumulated — lives
   * here, so each tap moves the absolute target by one and the debounce sends
   * the final number once. One-below-current at 1 is 0, which the backend
   * treats as removing the line.
   */
  const addOne = (sku: string) => {
    changeQuantity(sku, (quantitiesBySku[sku] ?? 0) + 1)
  }

  const removeOne = (sku: string) => {
    const current = quantitiesBySku[sku] ?? 0
    if (current > 0) changeQuantity(sku, current - 1)
  }

  /**
   * What a story's CTA does. Real promotions all map to `browse` (close and
   * stay on the catalog); the other cases remain for the design's fixture
   * actions and keep the switch exhaustive.
   *
   * `visit` sends the shopper to the account screen, where the design puts the
   * visit day. `category` clears the filter for an unknown name rather than
   * leaving the shopper on an empty grid.
   */
  const runStoryAction = (story: Story) => {
    setStoryIndex(null)
    switch (story.action) {
      case 'addSuggested':
        addSuggested()
        break
      case 'payment':
        navigate(screenPath('payment', { slug, sessionId }))
        break
      case 'category':
        browse.setCategory(
          story.categoryName && chips.includes(story.categoryName) ? story.categoryName : ALL,
        )
        break
      case 'visit':
        navigate(screenPath('account', { slug, sessionId }))
        break
      case 'browse':
        // A storefront-wide promotion has nowhere specific to send the shopper;
        // closing the reel leaves them on the catalog to spend against it.
        break
    }
  }

  /**
   * Where a banner tap lands. Only a banner that resolved a destination is a
   * button at all, so there is no "nowhere" case to handle here — `home/banners`
   * has already refused every reference it could not read.
   */
  const openBanner = (destination: BannerDestination) => {
    switch (destination.kind) {
      case 'product':
        navigate(screenPath('productDetail', { slug, sessionId, sku: destination.sku }))
        break
      case 'category':
        browse.setCategory(destination.name)
        break
      case 'externalLink':
        // `noopener,noreferrer`: the destination is authored in the storefront
        // admin, not by us, and a new tab that keeps `window.opener` can
        // navigate the storefront it came from.
        window.open(destination.url, '_blank', 'noopener,noreferrer')
        break
    }
  }

  const header = (
    <Stack
      component="header"
      data-testid="catalog-header"
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 3,
        px: 2,
        pt: 1.25,
        pb: 1.5,
        bgcolor: YALO_SEMANTIC.bgCanvas,
        // Flat while the page rests at the top; the shadow appears the moment
        // content slides underneath, which is the design's scroll cue.
        boxShadow: scrolled ? YALO_SHADOW.sm : 'none',
        transition: 'box-shadow 150ms ease',
      }}
    >
      {logoUrl && (
        <Box
          component="img"
          src={logoUrl}
          alt=""
          sx={{ width: 30, height: 30, objectFit: 'contain' }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.2px' }}>
          {storeName}
        </Typography>
        <Typography sx={{ fontSize: 12 }} color="text.secondary">
          Distribuidor:{' '}
          <Box component="span" sx={{ fontWeight: 600, color: YALO_SEMANTIC.fg2 }}>
            {distributor.name}
          </Box>
        </Typography>
      </Box>
      <Box
        component={RouterLink}
        to={screenPath('cart', { slug, sessionId })}
        aria-label={unitsInCart > 0 ? `Carrito, ${articlesText(unitsInCart)}` : 'Carrito'}
        sx={{
          position: 'relative',
          width: 42,
          height: 42,
          flex: 'none',
          borderRadius: `${YALO_RADIUS.pill}px`,
          bgcolor: YALO_SEMANTIC.bgSurface,
          border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
          color: YALO_SEMANTIC.fg1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': { bgcolor: YALO_SEMANTIC.bgSunken },
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 24 24"
          sx={{
            width: 20,
            height: 20,
            fill: 'none',
            stroke: 'currentColor',
            strokeWidth: 1.8,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
          }}
        >
          <CartGlyph />
        </Box>
        {unitsInCart > 0 && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              top: -3,
              right: -3,
              minWidth: 18,
              height: 18,
              px: '4px',
              borderRadius: `${YALO_RADIUS.pill}px`,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: 10.5,
              fontWeight: 700,
              lineHeight: '18px',
              textAlign: 'center',
            }}
          >
            {unitsInCart}
          </Box>
        )}
      </Box>
    </Stack>
  )

  const body = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Cargando catálogo" />
        </Box>
      )
    }

    if (error) {
      return (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      )
    }

    // The boot page having nothing is the storefront being empty. A *filtered*
    // view with nothing is a different sentence, and it belongs below the
    // controls so the shopper can undo the filter that emptied it.
    if ((data?.catalog ?? []).length === 0) {
      return <Typography sx={{ p: 2 }}>Este catálogo no tiene productos.</Typography>
    }

    return (
      <>
        <Stack direction="row" spacing={1} sx={{ px: 2, pt: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CatalogSearch value={browse.searchTerm} onChange={browse.setSearchTerm} />
          </Box>
          <Badge
            color="primary"
            badgeContent={browse.brands.length}
            // The count is already in the button's accessible name; announcing
            // it twice is how a screen reader reads "Filtros 2 2".
            slotProps={{ badge: { 'aria-hidden': true } }}
          >
            <IconButton
              onClick={() => setFiltersOpen(true)}
              aria-label={filtersLabel}
              sx={{
                width: 46,
                height: 46,
                borderRadius: `${YALO_RADIUS.pill}px`,
                bgcolor: YALO_SEMANTIC.bgSurface,
                border: `1px solid ${
                  browse.brands.length > 0 ? YALO_SEMANTIC.fg1 : YALO_SEMANTIC.borderDefault
                }`,
                color: YALO_SEMANTIC.fg1,
              }}
            >
              <Box
                component="svg"
                viewBox="0 0 24 24"
                sx={{
                  width: 20,
                  height: 20,
                  fill: 'none',
                  stroke: 'currentColor',
                  strokeWidth: 1.8,
                  strokeLinecap: 'round',
                }}
              >
                <FilterGlyph />
              </Box>
            </IconButton>
          </Badge>
        </Stack>

        {/* Stories and the suggested basket are browsing aids; while a query
            is live they would only push the results out of view. */}
        {!browse.searching && (
          <>
            {/* No banners, no rail. They come first because that is the slot
                the storefront merchandises — cinnamon puts them directly under
                the catalogue header too — and because they are the only block
                here whose content someone authored by hand. */}
            {hasBanners && (
              <Box sx={{ pt: 2, pb: 1 }}>
                <BannerRail banners={banners} onSelect={openBanner} />
              </Box>
            )}

            {/* No promotions, no rail — the section header would otherwise
                announce reels that are not there. */}
            {hasStories && (
              <Section eyebrow="Promos de la semana">
                <StoryReel stories={stories} onOpen={setStoryIndex} />
              </Section>
            )}

            {/* Two standing offers, and a storefront can have neither.
                With nothing above them they supply their own gap from the
                search bar — whichever of the banner rail and the reel is
                present was the only thing separating them. */}
            {(repeatLastOrder.order !== null || suggested.lines.length > 0) && (
              <Stack spacing={1.5} sx={{ px: 2, pt: hasStories || hasBanners ? 0 : 2 }}>
                {/* No previous order, no card — and above all no date. This is
                    the one place in the storefront that can say "del 6 ago",
                    because the date and the lines behind the button are the
                    same order; a customer who has never bought anything gets
                    silence rather than a sentence about a purchase that never
                    happened. */}
                {repeatLastOrder.order && (
                  <RepeatLastOrder
                    order={repeatLastOrder.order}
                    checked={repeatLastOrder.checked}
                    available={repeatLastOrder.available}
                    unavailable={repeatLastOrder.unavailable}
                    busy={repeatLastOrder.busy}
                    onRepeat={() => void repeatLastOrder.repeat()}
                  />
                )}

                {/* No recommendation, no basket. The storefront either has none
                    configured or has nothing to suggest to this customer, and a
                    basket assembled from the top of the catalog would be a
                    suggestion nobody made. */}
                {suggested.lines.length > 0 && (
                  <SuggestedOrder
                    model={suggested}
                    showPrices={!presentation.hidePrices}
                    subtitle={basket.source ? BASKET_SUBTITLE[basket.source] : ''}
                    onAddAll={() => void addSuggested()}
                    disabled={addingBasket}
                  />
                )}
              </Stack>
            )}
          </>
        )}

        {/* No categories, no rail: the storefront either has none or the query
            for them failed, and two chips that both mean "everything" read as a
            broken filter. */}
        {categories.length > 0 && (
          <Section eyebrow="Categorías">
            <CategoryChips
              categories={chips}
              selected={browse.category}
              onSelect={browse.setCategory}
            />
          </Section>
        )}

        {/* A landmark, not a wrapper: the same product can appear both here and
            in the suggested basket, so "the grid" has to be something a reader
            — and a test — can address. */}
        <Box component="section" aria-label="Catálogo">
          <Section
            eyebrow={gridEyebrow}
            action={
              <Typography sx={{ fontSize: 12 }} color="text.secondary">
                {gridCount}
              </Typography>
            }
          >
            {browse.error && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {browse.error.message}
              </Alert>
            )}

            {browse.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress aria-label="Cargando productos" />
              </Box>
            ) : visible.length === 0 ? (
              <Typography color="text.secondary">{emptyMessage}</Typography>
            ) : (
              <>
                <Grid container spacing={1.5}>
                  {visible.map((model) => (
                    <Grid key={model.sku} size={{ xs: 6 }}>
                      <CatalogCard
                        model={model}
                        onAdd={addOne}
                        onRemoveOne={removeOne}
                        onOpen={(sku) =>
                          navigate(screenPath('productDetail', { slug, sessionId, sku }))
                        }
                      />
                    </Grid>
                  ))}
                </Grid>

                {/* The sentinel is what an `IntersectionObserver` watches; the
                    button behind it is what a keyboard — and a browser without
                    the observer — uses to reach the same page. */}
                {browse.hasMore && (
                  <Box
                    ref={sentinel}
                    sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}
                  >
                    {browse.loadingMore ? (
                      <CircularProgress size={24} aria-label="Cargando más productos" />
                    ) : (
                      <Button variant="outlined" onClick={browse.loadMore}>
                        Ver más productos
                      </Button>
                    )}
                  </Box>
                )}
              </>
            )}
          </Section>
        </Box>
      </>
    )
  }

  return (
    <Screen testId="catalog-page">
      {header}

      {/* A failed add or remove is otherwise invisible: the mutation is
          fire-and-forget and the card's own state never changes. The basket's
          failure joins them here rather than getting its own alert — one place
          on the screen says what went wrong with the cart. */}
      {(basketError ?? updateError ?? repeatLastOrder.error) && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {(basketError ?? updateError ?? repeatLastOrder.error)?.message}
        </Alert>
      )}

      <Box sx={{ flex: 1, pb: 2 }}>{body()}</Box>

      {unitsInCart > 0 && (
        <Box
          component={RouterLink}
          to={screenPath('cart', { slug, sessionId })}
          sx={{
            position: 'sticky',
            bottom: 88,
            zIndex: 4,
            mx: 2,
            height: 50,
            borderRadius: `${YALO_RADIUS.pill}px`,
            bgcolor: YALO_SEMANTIC.statusSuccess,
            color: YALO_SEMANTIC.fg1,
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2.5,
            fontWeight: 700,
            fontSize: 14.5,
            boxShadow: YALO_SHADOW.md,
          }}
        >
          <Box component="span">Realizar pedido · {articlesText(unitsInCart)}</Box>
          {!presentation.hidePrices && (
            <Box component="span">{toPriceText(data?.cart?.total ?? 0, presentation)}</Box>
          )}
        </Box>
      )}

      <BottomNav
        slug={slug}
        sessionId={sessionId}
        cartCount={unitsInCart}
        // The centre "Promos" button opens the reels; with none to open it is
        // omitted, and BottomNav drops the control entirely.
        {...(hasStories ? { onOpenStories: () => setStoryIndex(0) } : {})}
      />

      <StoryViewer
        stories={stories}
        index={storyIndex}
        onIndexChange={setStoryIndex}
        onClose={() => setStoryIndex(null)}
        onAction={runStoryAction}
        sourceName={distributor.name}
      />

      <FilterSheet
        open={filtersOpen}
        brands={brandOptions}
        loading={brandsLoading}
        selected={browse.brands}
        onClose={() => setFiltersOpen(false)}
        onApply={(brands) => {
          browse.setBrands(brands)
          setFiltersOpen(false)
        }}
      />
    </Screen>
  )
}
